import axios from 'axios';
import { ACTIVE_URL_UAE_PASS } from 'aurora-utils/constants';
import errorMapper from './middlewareConnect/errorMapper';
import { BASE_URL } from './urls';

const { BASE_URL_UAE_PASS_LOGOUT } = ACTIVE_URL_UAE_PASS;

export const CONTENT_TYPE = {
  JSON: 'application/json',
  MULTIPART: 'multipart/form-data',
};

export default class API {
  constructor(
    baseURL,
    token,
    config = {
      headers: { contentType: CONTENT_TYPE.JSON },
    },
    tokenType = 'Bearer',
  ) {
    this.token = token;
    this.config = {
      baseURL: baseURL || BASE_URL,
      headers: {
        'Content-Type': config?.headers?.contentType ? config.headers.contentType : CONTENT_TYPE.JSON,
      },
      timeout: 60000,
      ...(config.responseType && { responseType: config.responseType }),
    };
    this.instance = axios.create(this.config);

    // Request interceptor for API calls
    this.instance.interceptors.request.use(async axiosConfig => {
      const accessToken = localStorage.getItem('access_token');
      const originalConfig = {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          Authorization: `${tokenType} ${this.token || accessToken}`,
        },
      };
      return originalConfig;
    });

    // Response interceptor for API calls
    this.instance.interceptors.response.use(
      response => {
        if (response.status === 200 || response.status === 206) {
          return response.data;
        }
        throw errorMapper(response);
      },
      async error => {
        const originalRequest = error.config;
        const refreshToken = localStorage.getItem('refresh_token');
        if (error.response?.status === 401 && refreshToken === null) {
          const tokenBasic = Buffer.from(`guest-client:guest-secret`, 'utf8').toString('base64');
          const bodyFormData = new FormData();
          bodyFormData.append('grant_type', 'client_credentials');
          try {
            const newToken = await axios.post(BASE_URL + '/authentication-service/oauth2/token', bodyFormData, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Basic ' + tokenBasic,
              },
            });
            localStorage.setItem('access_token', newToken.data.access_token);
            axios.defaults.headers.common.Authorization = 'Bearer ' + newToken.data.access_token;
            originalRequest.headers.Authorization = 'Bearer ' + newToken.data.access_token;
            const response = await axios(originalRequest);
            return response?.data;
          } catch (tokenError) {
            throw errorMapper(tokenError);
          }
        } else if (error.response?.status === 401 && refreshToken !== null) {
          const tokenBasic = Buffer.from(`my-client:my-secret`, 'utf8').toString('base64');
          const bodyFormData = new FormData();
          bodyFormData.append('grant_type', 'refresh_token');
          bodyFormData.append('refresh_token', refreshToken);
          try {
            const newToken = await axios.post(BASE_URL + '/authentication-service/oauth2/token', bodyFormData, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Basic ' + tokenBasic,
              },
            });
            localStorage.setItem('access_token', newToken.data.access_token);
            localStorage.setItem('refresh_token', newToken.data.refresh_token);
            axios.defaults.headers.common.Authorization = 'Bearer ' + newToken.data.access_token;
            originalRequest.headers.Authorization = 'Bearer ' + newToken.data.access_token;
            const response = await axios(originalRequest);
            return response?.data;
          } catch (tokenError) {
            if (tokenError.response?.status === 401 || tokenError.response?.data?.error === 'invalid_grant') {
              const lang = localStorage.getItem('lang');
              if (localStorage.getItem('login_with') === 'uaePass') {
                localStorage.clear();
                localStorage.setItem('lang', lang);
                localStorage.setItem('login_with', 'GuestLogin');
                window.location.href = BASE_URL_UAE_PASS_LOGOUT;
              }
              localStorage.clear();
              localStorage.setItem('lang', lang);
              localStorage.setItem('login_with', 'GuestLogin');
              window.location.href = '/login';
            }
            throw errorMapper(tokenError);
          }
        } else {
          throw errorMapper(error);
        }
      },
    );
  }

  get(url, id, params) {
    let endpoint = url;
    if (id) {
      endpoint += `/${id}`;
    }
    return this.instance.get(endpoint, { params });
  }

  post(url, body, params, id) {
    let endpoint = url;
    if (id) {
      endpoint += `/${id}`;
    }
    return this.instance.post(endpoint, body, { params });
  }

  delete(url, id, params) {
    let endpoint = url;
    if (id) {
      endpoint += `/${id}`;
    }
    return this.instance.delete(endpoint, { params });
  }

  deleteWithBody(url, id, params) {
    let endpoint = url;
    if (id) {
      endpoint += `/${id}`;
    }
    return this.instance.delete(endpoint, { data: { ...params } });
  }

  put(url, body, id) {
    let endpoint = url;
    if (id) {
      endpoint += `/${id}`;
    }
    return this.instance.put(endpoint, body);
  }

  patch(url, body) {
    return this.instance.patch(url, body);
  }
}
